import React from 'react';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';

import { Modal, List, Button, Typography, Pagination, Input, Image, Tabs, Spin, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import { useVideoHistory } from '../../hooks/carousel/use-list-videos';
import { useInstagramReels } from '../../hooks/media/use-instagram-reels';

import { removeOptimizedPath } from '../../utils/media-utils';

import { VideoMetadata } from '../../../../types/videos';
import { InstagramReel } from '../../../../types/instagram-reel';
import { useWidgetContext } from '../../context/widget';

dayjs.extend(LocalizedFormat);

const PAGE_SIZE = 6;
interface ReuseVideosModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (video: VideoMetadata | InstagramReel) => void;
}

export const ReuseVideosModal: React.FC<ReuseVideosModalProps> = ({ visible, onClose, onSelect }) => {
  const { selectedWidgetId } = useWidgetContext();
  const { fetchVideos } = useVideoHistory();
  const { fetchReels, updateReels } = useInstagramReels();

  const [activeTab, setActiveTab] = React.useState<'history' | 'instagram-reels'>('history');
  const [progress, setProgress] = React.useState<number>(0);

  // Historial de videos
  const [historyVideos, setHistoryVideos] = React.useState<VideoMetadata[]>([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [loadingHistory, setLoadingHistory] = React.useState(true);
  const [searchTerm, setSearchTerm] = React.useState('');

  // Videos de Instagram Reels
  const [instagramReels, setInstagramReels] = React.useState<InstagramReel[]>([]);
  const [currentReelsPage, setCurrentReelsPage] = React.useState(1);
  const [loadingReels, setLoadingReels] = React.useState(true);
  const [updatingReels, setUpdatingReels] = React.useState(false);

  const startIndex = (currentPage - 1) * PAGE_SIZE;

  const filteredVideos = historyVideos.filter(
    video =>
      !!(
        video.sk.toLowerCase().includes(searchTerm.toLowerCase()) ||
        video.caption?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        video.date?.includes(searchTerm)
      )
  );

  const [filterHandle, setFilterHandle] = React.useState<string>('all');

  function extractHandleFromPk(pk: string): string {
    // pk: CLIENT#123#INSTAGRAM#handle
    const parts = pk.split('#');
    return parts[parts.length - 1]; // último segmento es el handle
  }

  const displayedReels = React.useMemo(() => {
    if (filterHandle === 'all') return instagramReels;

    return instagramReels.filter(r => extractHandleFromPk(r.pk) === filterHandle);
  }, [instagramReels, filterHandle]);

  const currentVideos = filteredVideos.slice(startIndex, startIndex + PAGE_SIZE);

  const cardStyle: React.CSSProperties = {
    display: 'flex',
    border: '1px solid #e8e8e8',
    borderRadius: '8px',
    position: 'relative',
    height: '160px', // Altura fija para todas las tarjetas
    overflow: 'hidden'
  };

  // Estilos fijos para las tarjetas
  const imageContainerStyle: React.CSSProperties = {
    width: '101.25px',
    height: '160px', // Altura fija
    flexShrink: 0, // Evita que se encoja
    marginRight: '16px',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover', // Fuerza que la imagen cubra todo el espacio
    objectPosition: 'center'
  };

  const contentStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
    overflow: 'hidden',
    paddingRight: '24px'
  };

  const bottomActionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginTop: 'auto',
    overflow: 'hidden',
    flexWrap: 'wrap',
    height: '50%'
  };

  const truncatedTextStyle: React.CSSProperties = {
    marginTop: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    width: '100%',
    wordBreak: 'break-word',
    lineHeight: '1.5em',
    maxHeight: '4.5em'
  };

  const buttonStyle: React.CSSProperties = {
    height: '28px',
    fontSize: '11px',
    padding: '0 10px'
  };

  const linkStyle: React.CSSProperties = {
    maxWidth: '90%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  };

  const handleTabChange = (key: string) => {
    if (key === 'history' || key === 'instagram-reels') {
      setActiveTab(key);
    }
  };

  const loadVideos = React.useCallback(async () => {
    setLoadingHistory(true);
    try {
      const historyVideos = await fetchVideos(selectedWidgetId);
      setHistoryVideos(historyVideos || []);
    } catch (err) {
      console.error('Edit Form API Error:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [fetchVideos, selectedWidgetId]);

  React.useEffect(() => {
    if (visible && activeTab === 'history') {
      loadVideos();
    }
  }, [loadVideos, visible, activeTab]);

  const loadReels = React.useCallback(async () => {
    if (!selectedWidgetId) {
      console.warn('No widget seleccionado, no se pueden obtener reels');
      return;
    }

    setLoadingReels(true);
    try {
      const res = await fetchReels(selectedWidgetId);
      setInstagramReels(res);
    } catch (error) {
      console.error('Error obteniendo reels:', error);
    } finally {
      setLoadingReels(false);
    }
  }, [fetchReels, selectedWidgetId]);

  React.useEffect(() => {
    if (visible && activeTab === 'instagram-reels') {
      loadReels();
    }
  }, [visible, activeTab, loadReels]);

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      style={{ height: '100vh', top: '5vh' }}
      styles={{ body: { overflowX: 'hidden', maxHeight: '100vh' } }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        style={{ marginRight: '10px' }}
        items={[
          {
            key: 'history',
            label: 'Videos Históricos',
            children: (
              <>
                <Input
                  placeholder='Buscar en el historial...'
                  allowClear
                  onChange={e => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ width: 200, marginBottom: '16px' }}
                  suffix={<SearchOutlined style={{ color: '#000' }} />}
                />
                <List
                  grid={{ gutter: 16, column: 2 }}
                  dataSource={currentVideos}
                  loading={loadingHistory}
                  renderItem={video => (
                    <List.Item>
                      <div style={cardStyle}>
                        <div style={imageContainerStyle}>
                          <Image
                            src={removeOptimizedPath(video.posterUrl ?? '')}
                            style={imageStyle}
                            preview={false}
                            width='100%'
                            height='100%'
                          />
                        </div>
                        <div style={contentStyle}>
                          <div style={{ height: '50%' }}>
                            <Typography.Text type='secondary'>
                              Publicado: {dayjs(video.date).format('LL')}
                            </Typography.Text>

                            <div style={{ ...truncatedTextStyle, color: 'rgb(70, 70, 70)', fontSize: 12 }}>
                              {video.caption || 'Sin título disponible'}
                            </div>
                          </div>

                          <div style={bottomActionsStyle}>
                            <Typography.Link href={video.videoUrl} target='_blank' style={linkStyle}>
                              {video.originalUri || video.videoUrl}
                            </Typography.Link>
                            <Button type='primary' onClick={() => onSelect(video)} style={buttonStyle}>
                              Seleccionar
                            </Button>
                          </div>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
                <Pagination
                  current={currentPage}
                  pageSize={PAGE_SIZE}
                  total={filteredVideos.length}
                  onChange={setCurrentPage}
                  showSizeChanger={false}
                  style={{ textAlign: 'center' }}
                />
              </>
            )
          },
          {
            key: 'instagram-reels',
            label: 'Reels de Instagram',
            children: (
              <Spin spinning={updatingReels} tip={`Cargando reels desde: ${progress}%`}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}
                >
                  <Button
                    type='default'
                    loading={updatingReels}
                    onClick={async () => {
                      if (!selectedWidgetId) {
                        console.warn('No widget seleccionado, no se pueden actualizar reels');
                        return;
                      }

                      setUpdatingReels(true);
                      setLoadingReels(true);
                      setProgress(0);

                      try {
                        await updateReels(selectedWidgetId);

                        let current = 0;
                        const interval = setInterval(() => {
                          current += 1;
                          setProgress(current);

                          if (current >= 100) {
                            clearInterval(interval);
                            fetchReels(selectedWidgetId).then(newReels => {
                              setInstagramReels(newReels);
                              setLoadingReels(false);
                              setUpdatingReels(false);
                              setProgress(0);
                            });
                          }
                        }, 700);
                      } catch (error) {
                        console.error('Error actualizando reels:', error);
                        setLoadingReels(false);
                        setUpdatingReels(false);
                        setProgress(0);
                      }
                    }}
                  >
                    Actualizar reels
                  </Button>

                  {/* filtro selector */}
                  <Select
                    value={filterHandle}
                    onChange={value => setFilterHandle(value)}
                    style={{ width: 220 }}
                    options={[
                      { value: 'all', label: 'Todos' },
                      ...Array.from(new Set(instagramReels.map(r => extractHandleFromPk(r.pk)))).map(h => ({
                        value: h,
                        label: h
                      }))
                    ]}
                  />
                </div>

                <List
                  grid={{ gutter: 16, column: 2 }}
                  dataSource={displayedReels.slice((currentReelsPage - 1) * PAGE_SIZE, currentReelsPage * PAGE_SIZE)}
                  loading={loadingReels && !updatingReels}
                  renderItem={reel => (
                    <List.Item>
                      <div style={cardStyle}>
                        <div style={imageContainerStyle}>
                          <Image src={reel.displayUrl} style={imageStyle} preview={false} width='100%' height='100%' />
                        </div>
                        <div style={contentStyle}>
                          <div style={{ height: '50%' }}>
                            <Typography.Text type='secondary'>
                              Publicado: {dayjs(reel.timestamp).format('LL')}
                            </Typography.Text>
                            <div
                              style={{
                                ...truncatedTextStyle,
                                color: 'rgb(70, 70, 70)',
                                fontSize: 12
                              }}
                            >
                              {reel.caption || 'Sin título disponible'}
                            </div>
                          </div>
                          <div style={bottomActionsStyle}>
                            <Typography.Link href={reel.url} target='_blank' style={linkStyle}>
                              {reel.url}
                            </Typography.Link>
                            <Button type='primary' onClick={() => onSelect(reel)} style={buttonStyle}>
                              Seleccionar
                            </Button>
                          </div>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />

                <Pagination
                  current={currentReelsPage}
                  pageSize={PAGE_SIZE}
                  total={displayedReels.length}
                  onChange={setCurrentReelsPage}
                  showSizeChanger={false}
                  style={{ textAlign: 'center' }}
                />
              </Spin>
            )
          }
        ]}
      />
    </Modal>
  );
};
